import { bisect } from 'd3-array';

import {
  endOfDay,
  endOfHour,
  endOfISOWeek,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  isValid,
  parseISO,
  startOfDay,
  startOfHour,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  StartOfWeekOptions,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { format, formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

import { isObject, omitBy } from 'lodash';

import { TimePrecision, TimeRange, timePrecision } from './types';
import { TimeAggregationPeriod } from '../explore/types';

export const DateFormat = 'yyyy-MM-dd';
export const DateTimeFormat = "yyyy-MM-dd'T'HH:mm";
export const DateFriendlyFormat = 'LLL d yyyy';
export const DateFriendlyTimeFormat = 'LLL d yyyy, HH:mm';

// Format "yyyy-MM-dd" or "yyyy-MM-dd'T'HH:mm"
export interface DateRange {
  from: string;
  to: string;
}

interface Interval {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
}

export const isDateRange = (value: unknown): value is DateRange =>
  typeof value === 'object' && value !== null && 'from' in value && 'to' in value;

export const getHumanReadableRange = (value: Partial<DateRange>, timezone: string) => {
  const from = value.from ?? '-';
  const fromIncludesTime = includesTime(from);
  const fromFormat = fromIncludesTime ? DateFriendlyTimeFormat : DateFriendlyFormat;
  const fromFormatted = formatInTimeZone(from, timezone, fromFormat);

  const to = value.to ?? '-';
  const toIncludesTime = includesTime(to);
  const toFormat = toIncludesTime ? DateFriendlyTimeFormat : DateFriendlyFormat;
  const toFormatted = formatInTimeZone(to, timezone, toFormat);

  if (fromFormatted === toFormatted) {
    return fromFormatted;
  }

  return `${fromFormatted} – ${toFormatted}`;
};

export const getISOFormatForPrecision = (
  precision: Exclude<TimeAggregationPeriod, 'day_of_week'>,
) => (precision === 'hour' ? DateTimeFormat : DateFormat);

const getLocalDayNames = (
  locale = 'en',
  format: 'long' | 'short' | 'narrow' | undefined = 'long',
) => {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: format, timeZone: 'UTC' });
  const days = [2, 3, 4, 5, 6, 7, 1].map((day) => {
    const dd = day < 10 ? `0${day}` : day;
    return new Date(`2017-01-${dd}T00:00:00+00:00`);
  });
  return days.map((date) => formatter.format(date));
};

const localDayNames = getLocalDayNames();

export const getWeekdayName = (dow: number) => localDayNames[dow - 1];

export const combineDateTime = (date: string, time?: string) =>
  `${date}${time !== '' && time !== undefined ? `T${time}` : ''}`;

/**
 * Format a date for display in a given timezone.
 * @param date The Date, ISO string or timestamp to format.
 */
export const formatDate = (
  date: string | number | Date,
  fmt: string,
  timezone?: string,
): string => {
  return format(timezone !== undefined ? toZonedTime(date, timezone) : new Date(date), fmt, {
    timeZone: timezone,
  });
};

export const formatLocalDateTime = (date: string | number | Date): string =>
  formatDate(date, 'MMMM do yyyy HH:mm:ss z');

const IntervalFormats: { [key in TimeAggregationPeriod]: string } = {
  year: 'yyyy',
  quarter: 'QQQ yyyy',
  month: 'MMMM yyyy',
  week: "'W'I RRRR",
  day_of_week: 'EEEE',
  day: 'MMM do yyyy',
  hour: 'MMM do yyyy HH:mm',
};

export const getIntervalFormat = (timeInterval?: TimeAggregationPeriod) => {
  return timeInterval !== undefined ? IntervalFormats[timeInterval] : 'MMMM yyyy HH:mm:ss OOOO';
};

export function getFormattedDateForPeriod(
  date: Date,
  aggPeriod: TimeAggregationPeriod | null,
  timezone?: string,
) {
  date = timezone !== undefined ? toZonedTime(date, timezone) : date;
  if (aggPeriod === null) {
    return formatLocalDateTime(date);
  }

  if (aggPeriod === 'week') {
    const startOf = startOfISOWeek(date);
    const endOf = endOfISOWeek(date);
    if (
      startOf.getFullYear() !== new Date().getFullYear() ||
      endOf.getFullYear() !== new Date().getFullYear()
    ) {
      // prettier-ignore
      return `${formatDate(startOf, 'MMM do yyyy')}–${formatDate(endOf, 'MMM do yyyy')}`;
    }
    return `${formatDate(startOf, 'MMM do')}–${formatDate(endOf, 'MMM do')}`;
  }
  return formatDate(date, getIntervalFormat(aggPeriod));
}

export const dateSort = (a: Date, b: Date) => a.getTime() - b.getTime();

export const findClosestDate = (values: Date[], target: Date): Date | undefined =>
  values[bisect(values, target)];

function assertNeverPreset(timeRangePreset: never): never {
  throw new Error(`Unhandled timeRangePreset: ${timeRangePreset}`);
}

export const DateRangeOptions: { label: string; value: TimeRange }[] = [
  { label: 'Custom', value: 'custom' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'Last 1 year', value: '1y' },
  { label: 'Year to date', value: 'ytd' },
  { label: 'Month to date', value: 'mtd' },
  { label: `Last year (${subYears(new Date(), 1).getFullYear()})`, value: 'prevyear' },
  { label: `Last month (${format(subMonths(new Date(), 1), 'MMMM')})`, value: 'prevmonth' },
];

export function getFromDate(timeRangePreset: TimeRange, aggPeriod: TimePrecision) {
  switch (timeRangePreset) {
    case '1y': {
      switch (aggPeriod) {
        case timePrecision.enum.Daily:
          return startOfDay(subYears(new Date(), 1));
        case timePrecision.enum.Weekly:
          return startOfWeek(subYears(new Date(), 1));
        case timePrecision.enum.Monthly:
        default:
          return startOfMonth(subYears(new Date(), 1));
      }
    }
    case '7d':
      return startOfDay(subDays(new Date(), 7));
    case '30d':
      return startOfDay(subDays(new Date(), 30));
    case '90d':
    case 'custom':
      return startOfDay(subDays(new Date(), 90));
    case 'ytd':
      return startOfYear(new Date());
    case 'mtd':
      return startOfMonth(new Date());
    case 'prevmonth':
      return startOfMonth(subMonths(new Date(), 1));
    case 'prevyear':
      return startOfYear(subYears(new Date(), 1));
    default:
      return assertNeverPreset(timeRangePreset);
  }
}

export function getToDate(timeRangePreset: TimeRange) {
  switch (timeRangePreset) {
    case '1y':
    case '7d':
    case '30d':
    case '90d':
    case 'ytd':
    case 'mtd':
    case 'custom':
      return new Date();
    case 'prevmonth':
      return endOfMonth(subMonths(new Date(), 1));
    case 'prevyear':
      return endOfYear(subYears(new Date(), 1));
    default:
      return assertNeverPreset(timeRangePreset);
  }
}

export const getDatesFromTimeRange = (timeRangePreset: TimeRange, aggPeriod: TimePrecision) => {
  return {
    startDate: getFromDate(timeRangePreset, aggPeriod),
    endDate: getToDate(timeRangePreset),
  };
};

const isoDateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
const isoDateRegex = /^\d{4}-\d{2}-\d{2}/;

const isISODateTimeString = (value: string) => isoDateTimeRegex.test(value);

const isISODateString = (value: string) => isoDateRegex.test(value);

export const containsISODateString = (value: string) =>
  isISODateString(value) || isISODateTimeString(value);

export const includesTime = (value: string): boolean => value.substring(11) !== '';

export const getDefaultFormattedLocalDate = () => format(new Date(), DateFormat);

export const ensureLocalDateFormat = (value: string): string => {
  const parsedValue = parseISO(value);
  return isValid(parsedValue)
    ? format(parsedValue, includesTime(value) ? DateTimeFormat : DateFormat)
    : getDefaultFormattedLocalDate();
};

export const formatLocalDate = (date: Date): string => formatDate(date, DateFormat);

export const isInterval = (value: unknown): value is Interval =>
  isObject(value) &&
  Object.keys(value).every((key) =>
    ['years', 'months', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'].includes(key),
  );

const IntervalConversions = [
  { from: 'milliseconds', to: 'seconds', factor: 1000 },
  { from: 'seconds', to: 'minutes', factor: 60 },
  { from: 'minutes', to: 'hours', factor: 60 },
  { from: 'hours', to: 'days', factor: 24 },
  { from: 'days', to: 'months', factor: 30 },
  { from: 'months', to: 'years', factor: 12 },
];

export const justifyInterval = (interval: Interval) => {
  const justifiedInterval = IntervalConversions.reduce(
    (memo, { from, to, factor }) => {
      const fromValue = memo[from as keyof Interval];
      const toValue = memo[to as keyof Interval] ?? 0;

      if (fromValue === undefined) {
        return memo;
      }

      if (Math.abs(fromValue) >= factor) {
        const quotient = Math.floor(Math.abs(fromValue) / factor);
        const remainder = Math.abs(fromValue) % factor;
        const sign = fromValue < 0 ? -1 : 1;

        return { ...memo, ...{ [to]: toValue + quotient * sign, [from]: remainder * sign } };
      }

      return memo;
    },
    { ...interval },
  );

  return omitBy(justifiedInterval, (value) => value === undefined || value === 0);
};

/**
 * Call a date-fns function in timezone.
 * @param date The date to convert.
 * @param tz The timezone to convert in.
 * @param fn The date-fns function to call.
 * @returns
 */
const calculateZonedDate = <
  T extends (date: Date | number | string, options?: StartOfWeekOptions) => Date,
>(
  date: Date | number | string,
  tz: string,
  fn: T,
  options?: StartOfWeekOptions,
): Date => fromZonedTime(fn(toZonedTime(date, tz), options), tz);

export function getDateRangeFromPrecision(
  date: Date,
  precision: TimeAggregationPeriod,
  timezone: string,
  options: StartOfWeekOptions = { weekStartsOn: 1 },
) {
  switch (precision) {
    case 'hour':
      return {
        startDate: calculateZonedDate(date, timezone, startOfHour),
        endDate: calculateZonedDate(date, timezone, endOfHour),
      };
    case 'day':
      return {
        startDate: calculateZonedDate(date, timezone, startOfDay),
        endDate: calculateZonedDate(date, timezone, endOfDay),
      };
    case 'week':
      return {
        startDate: calculateZonedDate(date, timezone, startOfWeek, options),
        endDate: calculateZonedDate(date, timezone, endOfWeek, options),
      };
    case 'month':
      return {
        startDate: calculateZonedDate(date, timezone, startOfMonth),
        endDate: calculateZonedDate(date, timezone, endOfMonth),
      };
    case 'quarter':
      return {
        startDate: calculateZonedDate(date, timezone, startOfQuarter),
        endDate: calculateZonedDate(date, timezone, endOfQuarter),
      };
    case 'year':
      return {
        startDate: calculateZonedDate(date, timezone, startOfYear),
        endDate: calculateZonedDate(date, timezone, endOfYear),
      };
    default:
      throw new Error(`Unsupported precision: ${precision}`);
  }
}
